import { create } from "zustand";
import type { AwaitingInput } from "@/stores/canvas-store";
import { mirroredStore } from "@/lib/mirrored-store";

export interface InputBarQuestionSnapshot {
  awaiting: AwaitingInput;
  position: number;
  total: number;
  /** Other known pending questions on the same frame, retained through loading. */
  remaining?: AwaitingInput[];
}
interface InputBarQuestionState {
  awaitingInputId: string | null;
  /** Keeps the selected question through project loading and late mirror hydration. */
  question: InputBarQuestionSnapshot | null;
  replyingId: string | null;
  resolved: boolean;
  start: (id: string, question?: InputBarQuestionSnapshot) => void;
  finish: () => void;
  setReplying: (id: string | null) => void;
  resolve: (id: string) => void;
}
const empty = {
  awaitingInputId: null,
  question: null,
  replyingId: null,
  resolved: false,
};
export const useInputBarQuestionStore = create<InputBarQuestionState>()(
  mirroredStore("inputBarQuestion", (set, get) => ({
    ...empty,
    start: (awaitingInputId, question) =>
      set({ ...empty, awaitingInputId, question: question ?? null }),
    finish: () => set(empty),
    setReplying: (replyingId) => set({ replyingId }),
    resolve: (id) => {
      const state = get();
      if (state.awaitingInputId !== id) {
        const question = state.question;
        if (question?.remaining?.some((item) => item.id === id)) {
          const remaining = question.remaining.filter((item) => item.id !== id);
          set({
            question: {
              ...question,
              remaining,
              total: remaining.length + 1,
              position: Math.min(question.position, remaining.length + 1),
            },
          });
        }
        return;
      }
      // Every mounted bar waits for the same HTTP reply; a mirror must not
      // clear the selected card while the submitting window is advancing it.
      if (state.replyingId === id) set({ resolved: true });
      else set(empty);
    },
  })),
);

/** Shared by full snapshots and live mutations from another desktop window. */
export function hydrateInputBarQuestion(snapshot: unknown): void {
  if (!snapshot || typeof snapshot !== "object") return;
  const value = snapshot as Record<string, unknown>;
  const patch: Partial<InputBarQuestionState> = {};
  if (value.awaitingInputId === null) {
    useInputBarQuestionStore.setState(empty);
    return;
  }
  if (typeof value.awaitingInputId === "string")
    patch.awaitingInputId = value.awaitingInputId;
  if (value.question === null) patch.question = null;
  else if (value.question && typeof value.question === "object") {
    const question = value.question as InputBarQuestionSnapshot;
    if (
      question.awaiting &&
      typeof question.awaiting.id === "string" &&
      typeof question.awaiting.frameId === "string" &&
      typeof question.awaiting.question === "string" &&
      Array.isArray(question.awaiting.options)
    )
      patch.question = question;
  }
  if (value.replyingId === null || typeof value.replyingId === "string")
    patch.replyingId = value.replyingId;
  if (typeof value.resolved === "boolean") patch.resolved = value.resolved;
  useInputBarQuestionStore.setState(patch);
}
